import { CoreContext } from 'context/CoreContext'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Load, LoadCenter } from 'ui/styled'
import { StyledImg, StyledVideo, VideoContainer } from './styled'
import Wrapper from '../Wrapper';
import { Read } from 'services/core';
import { UpdateOnboardingVideoProgress, WELCOME_ONBOARDING_VIDEO_KEY } from 'services/me';
import { apiRequest, exposeStrapiError, parseStrapiImage } from 'utils';
import useVideoAnalytics from 'hooks/useVideoAnalytics';

const PROGRESS_INTERVAL_SECONDS = 5;


export default function ModalWelcome() {

  const { modal, reloadMe, user } = useContext(CoreContext);

  const [video, setVideo] = useState()
  const [loading, setLoading] = useState()
  const [played, setPlayed] = useState(true)
  const videoRef = useRef(null)
  const startedRef = useRef(false)
  const completedRef = useRef(modal?.progress?.completed === true)
  const lastProgressSecondRef = useRef(0)
  const videoKey = modal?.videoKey || WELCOME_ONBOARDING_VIDEO_KEY

  const videoUrl = useMemo(() => (
    video?.welcome_video?.url ? parseStrapiImage(video.welcome_video.url) : null
  ), [video?.welcome_video?.url])

  const videoName = video?.modal_title || video?.title || 'Onboarding Video'

  const videoAnalytics = useVideoAnalytics({
    eventPrefix: 'onboarding_video',
    metadata: {
      video_name: videoName,
      video_type: 'onboarding',
      user_id: user?.id,
      source: 'welcome_modal',
      screen: 'dashboard',
    },
    sessionKey: `${videoKey}:${videoUrl || ''}`,
  })

  const thumbUrl = useMemo(() => (
    video?.thumb?.[0]?.url ? parseStrapiImage(video.thumb[0].url) : null
  ), [video?.thumb])

  const init = async () => {
    setLoading(true)
    const result = await Read('onboardings');
    setVideo(result?.data?.[0])

    setLoading(false)

  }

  useEffect(() => { init() }, [])

  const getVideoSnapshot = useCallback(() => {
    const player = videoRef.current
    const watchedSeconds = Number.isFinite(player?.currentTime) ? player.currentTime : 0
    const durationSeconds = Number.isFinite(player?.duration) ? player.duration : 0

    return {
      watchedSeconds,
      durationSeconds,
    }
  }, [])

  const saveProgress = useCallback(async (event, { reportError = false } = {}) => {
    const snapshot = getVideoSnapshot()
    const result = await apiRequest(() => UpdateOnboardingVideoProgress({
      videoKey,
      event,
      watchedSeconds: snapshot.watchedSeconds,
      durationSeconds: snapshot.durationSeconds,
    }))

    if (reportError && exposeStrapiError(result)) return null

    if (result?.completed) {
      completedRef.current = true
      await reloadMe()
    }

    return result
  }, [getVideoSnapshot, reloadMe, videoKey])

  const handleClose = useCallback(async () => {
    if (!completedRef.current) {
      videoAnalytics.trackClosed(getVideoSnapshot())
    }
    await saveProgress('close')
  }, [getVideoSnapshot, saveProgress, videoAnalytics])

  const handleLoadedMetadata = useCallback(() => {
    const player = videoRef.current
    const watchedSeconds = Number(modal?.progress?.watchedSeconds || 0)

    if (!player || !watchedSeconds || modal?.progress?.completed) return

    const maxResumeSeconds = Number.isFinite(player.duration)
      ? Math.max(0, player.duration - 1)
      : watchedSeconds

    player.currentTime = Math.min(watchedSeconds, maxResumeSeconds)
    lastProgressSecondRef.current = Math.floor(player.currentTime)
  }, [modal?.progress])

  const handlePlay = useCallback(() => {
    videoAnalytics.trackStarted(getVideoSnapshot())
    if (startedRef.current) return
    startedRef.current = true
    saveProgress('start', { reportError: true })
  }, [getVideoSnapshot, saveProgress, videoAnalytics])

  const handlePause = useCallback(() => {
    if (completedRef.current) return
    saveProgress('pause')
  }, [saveProgress])

  const handleCompleted = useCallback(() => {
    if (completedRef.current) return
    completedRef.current = true
    videoAnalytics.trackCompleted({
      ...getVideoSnapshot(),
      progressPercent: 100,
    })
    saveProgress('completed', { reportError: true })
  }, [getVideoSnapshot, saveProgress, videoAnalytics])

  const handleTimeUpdate = useCallback(() => {
    const player = videoRef.current
    if (!player) return

    const currentSecond = Math.floor(player.currentTime || 0)
    const durationSeconds = Number.isFinite(player.duration) ? player.duration : 0
    const progressPercent = durationSeconds > 0 ? (player.currentTime / durationSeconds) * 100 : 0
    const snapshot = {
      watchedSeconds: player.currentTime || 0,
      durationSeconds,
      progressPercent,
    }

    videoAnalytics.trackProgress(snapshot)

    if (progressPercent >= 95) {
      handleCompleted()
      return
    }

    if (currentSecond - lastProgressSecondRef.current >= PROGRESS_INTERVAL_SECONDS) {
      lastProgressSecondRef.current = currentSecond
      saveProgress('progress')
    }
  }, [handleCompleted, saveProgress, videoAnalytics])

  useEffect(() => {
    if (!played && videoRef.current) {
      try {
        const playPromise = videoRef.current.play();
        if (playPromise && typeof playPromise.then === 'function') {
          playPromise.catch((e) => {
            console.warn('Video play failed:', e);
          });
        }
      } catch (e) {
        console.warn('Error attempting to play video:', e);
      }
    }
  }, [played]);

  useEffect(() => {
    if (!videoUrl) return;

    if (!played) return;

    const existing = document.querySelector(`link[rel="preload"][href="${videoUrl}"]`);
    if (existing) return;

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'video';
    link.href = videoUrl;
    link.type = 'video/mp4';
    document.head.appendChild(link);

    return () => {
      try {
        document.head.removeChild(link);
      } catch (e) { }
    };
  }, [videoUrl, played]);

  return (
    <>
      <Wrapper title={video?.modal_title} onClose={handleClose}>
        {(loading) ? <LoadCenter>
          <Load />
        </LoadCenter> : <>
          {/* <Title nomargin>{video?.modal_title}</Title> */}
          <VideoContainer>
            {!played ? null : (
              <StyledImg
                onClick={() => setPlayed(false)}
                style={thumbUrl ? { backgroundImage: `url(${thumbUrl})` } : null}
                role="img"
                aria-label={video?.modal_title || 'Onboarding thumbnail'}
              />
            )}
            {played ? null : (
              <StyledVideo
                ref={videoRef}
                controls
                preload="metadata"
                playsInline
                onLoadedMetadata={handleLoadedMetadata}
                onPlay={handlePlay}
                onPause={handlePause}
                onEnded={handleCompleted}
                onTimeUpdate={handleTimeUpdate}
              >
                <source src={videoUrl} type="video/mp4" />
                Seu navegador não suporta o elemento de vídeo.
              </StyledVideo>
            )}
          </VideoContainer>

          {/* <CompanyButtonContainer>
          <Button onClick={close}>Cancelar</Button>
          <Button color="primary" onClick={handleSave} between>Salvar</Button>
          </CompanyButtonContainer> */}
        </>}
      </Wrapper>
    </>
  )
}
